<template>
  <div class="order-detail-container">
    <el-card class="order-card">
      <div class="page-header">
        <h2>订单详情</h2>
        <div class="header-actions">
          <el-button v-if="orderForm.status !== '未结算' && isAdmin" type="primary" @click="handleEdit" style="margin-right: 10px;">编辑订单</el-button>
          <el-button @click="$router.back()">返回</el-button>
        </div>
      </div>
      
      <el-form ref="orderForm" :model="orderForm" label-width="120px" label-position="right">
        <el-divider content-position="left">
          <span class="divider-title">项目信息</span>
        </el-divider>
        
        <el-row :gutter="24">
          <el-col :span="12">
            <el-form-item label="项目名称" :prop="isEditMode ? 'projectName' : ''">
              <el-input v-model="orderForm.projectName" :disabled="!isEditMode"></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="订单号">
              <el-input v-model="orderForm.orderNo" disabled></el-input>
            </el-form-item>
          </el-col>
        </el-row>
        
        <el-row :gutter="24">
          <el-col :span="12">
            <el-form-item label="选择客户" :prop="isEditMode ? 'customerId' : ''">
              <template v-if="isEditMode">
                <el-select 
                  v-model="orderForm.customerId" 
                  filterable 
                  placeholder="请选择客户" 
                  style="width: 100%"
                  :loading="loading.customers"
                  @change="handleCustomerChange">
                  <el-option
                    v-for="item in customerOptions"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value">
                  </el-option>
                </el-select>
              </template>
              <el-input v-else v-model="orderForm.customer" disabled></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="联系人" :prop="isEditMode ? 'contact' : ''">
              <el-input v-model="orderForm.contact" :disabled="!isEditMode"></el-input>
            </el-form-item>
          </el-col>
        </el-row>
        
        <el-row :gutter="24">
          <el-col :span="12">
            <el-form-item label="联系方式" :prop="isEditMode ? 'phone' : ''">
              <el-input v-model="orderForm.phone" :disabled="!isEditMode"></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="地址" :prop="isEditMode ? 'address' : ''">
              <el-input v-model="orderForm.address" :disabled="!isEditMode"></el-input>
            </el-form-item>
          </el-col>
        </el-row>
        
        <el-row :gutter="24">
          <el-col :span="12">
            <el-form-item label="样品数量" :prop="isEditMode ? 'sampleCount' : ''">
              <template v-if="isEditMode">
                <el-input-number 
                  v-model="orderForm.sampleCount" 
                  :min="1" 
                  :max="999" 
                  placeholder="请输入样品数量" 
                  style="width: 100%">
                </el-input-number>
              </template>
              <el-input v-else v-model="orderForm.sampleCount" disabled></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="预期完成时间" :prop="isEditMode ? 'expectedCompletionDate' : ''">
              <template v-if="isEditMode">
                <el-date-picker
                  v-model="orderForm.expectedCompletionDate"
                  type="date"
                  placeholder="选择预期完成时间"
                  value-format="yyyy-MM-dd"
                  :picker-options="{
                    disabledDate(time) {
                      return time.getTime() < Date.now() - 8.64e7; // 禁止选择今天之前的日期
                    }
                  }"
                  style="width: 100%">
                </el-date-picker>
              </template>
              <el-input v-else v-model="orderForm.expectedCompletionDate" disabled></el-input>
            </el-form-item>
          </el-col>
        </el-row>
        
        <el-row :gutter="24">
          <el-col :span="12">
            <el-form-item label="订单状态">
              <el-tag :type="getStatusType(orderForm.status)" style="width: 100%; text-align: center;">{{ orderForm.status }}</el-tag>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="备注">
              <el-input type="textarea" v-model="orderForm.remark" :rows="3" :disabled="!isEditMode"></el-input>
            </el-form-item>
          </el-col>
        </el-row>
        
        <el-divider content-position="left">
          <span class="divider-title">商家信息</span>
        </el-divider>
        
        <el-row :gutter="24">
          <el-col :span="12">
            <el-form-item label="选择商家" :prop="isEditMode ? 'merchantId' : ''">
              <template v-if="isEditMode">
                <el-select 
                  v-model="orderForm.merchantId" 
                  filterable 
                  placeholder="请选择商家" 
                  style="width: 100%"
                  :loading="loading.merchants"
                  @change="handleMerchantChange">
                  <el-option
                    v-for="item in merchantOptions"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value">
                  </el-option>
                </el-select>
              </template>
              <el-input v-else v-model="orderForm.merchant" disabled></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="仪器信息" :prop="isEditMode ? 'deviceId' : ''">
              <template v-if="isEditMode">
                <el-select 
                  v-model="orderForm.deviceId" 
                  filterable 
                  placeholder="请选择仪器" 
                  style="width: 100%"
                  @change="handleEquipmentChange">
                  <el-option
                    v-for="item in equipmentOptions"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value">
                  </el-option>
                </el-select>
              </template>
              <el-input v-else v-model="orderForm.equipment" disabled></el-input>
            </el-form-item>
          </el-col>
        </el-row>
        
        <el-row :gutter="24" v-if="!isCustomerView">
          <el-col :span="12">
            <el-form-item label="参考价格">
              <el-input v-model="orderForm.referencePrice" disabled>
                <template slot="prepend">¥</template>
              </el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="实际价格" :prop="isEditMode ? 'actualPrice' : ''">
              <el-input v-model="orderForm.actualPrice" :disabled="!isEditMode">
                <template slot="prepend">¥</template>
              </el-input>
            </el-form-item>
          </el-col>
        </el-row>
        
        <el-row :gutter="24">
          <el-col :span="12">
            <el-form-item label="联系人" :prop="isEditMode ? 'merchantContact' : ''">
              <el-input v-model="orderForm.merchantContact" :disabled="!isEditMode"></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="联系方式" :prop="isEditMode ? 'merchantPhone' : ''">
              <el-input v-model="orderForm.merchantPhone" :disabled="!isEditMode"></el-input>
            </el-form-item>
          </el-col>
        </el-row>
        
        <el-row :gutter="24">
          <el-col :span="24">
            <el-form-item label="地址">
              <el-input v-model="orderForm.merchantAddress" :disabled="!isEditMode"></el-input>
            </el-form-item>
          </el-col>
        </el-row>
        
        <!-- 物流信息部分，移动到测试要求附件上面 -->
        <template v-if="orderForm.status !== '未邮寄'">
          <el-divider content-position="left">
            <span class="divider-title">物流信息</span>
          </el-divider>
          
          <el-row :gutter="24">
            <el-col :span="12">
              <el-form-item label="物流公司">
                <el-input v-model="orderForm.logisticsCompany" disabled></el-input>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="物流单号">
                <el-input v-model="orderForm.trackingNumber" disabled></el-input>
              </el-form-item>
            </el-col>
          </el-row>
          
          <el-row :gutter="24">
            <el-col :span="12">
              <el-form-item label="邮寄时间">
                <el-input v-model="orderForm.shipTime" disabled></el-input>
              </el-form-item>
            </el-col>
          </el-row>
        </template>
        
        <el-divider content-position="left">
          <span class="divider-title">测试要求附件</span>
        </el-divider>
        
        <template v-if="isEditMode">
          <el-form-item>
            <el-upload
              ref="upload"
              class="upload-demo"
              drag
              action=""
              :http-request="customUpload"
              multiple
              :headers="uploadHeaders"
              :on-preview="handlePreview"
              :on-remove="handleRemove"
              :on-error="handleError"
              :before-upload="beforeUpload"
              :file-list="fileList"
              :limit="10"
              :show-file-list="false">
              <i class="el-icon-upload"></i>
              <div class="el-upload__text">将文件拖到此处，或<em>点击上传</em></div>
              <div class="el-upload__tip" slot="tip">
                支持上传PDF、Word、Excel或图片格式文件，每个文件不超过10MB
                <span style="color: #F56C6C; margin-left: 10px;">必须先选择客户才能上传文件</span>
              </div>
            </el-upload>
          </el-form-item>
        </template>
        
        <div class="file-list-container" v-if="fileList.length > 0">
          <h3 class="file-list-title">附件列表</h3>
          <el-table
            :data="fileList"
            style="width: 100%"
            border>
            <el-table-column
              prop="name"
              label="文件名"
              min-width="300">
              <template slot-scope="scope">
                {{ scope.row.fileId || scope.row.name }}
              </template>
            </el-table-column>
            <el-table-column
              prop="size"
              label="大小"
              width="150">
              <template slot-scope="scope">
                {{ formatFileSize(scope.row.size) }}
              </template>
            </el-table-column>
            <el-table-column
              label="操作"
              width="150"
              align="center">
              <template slot-scope="scope">
                <el-button 
                  type="text" 
                  size="small" 
                  @click="handlePreview(scope.row)">预览</el-button>
                <el-button 
                  type="text" 
                  size="small" 
                  @click="handleDownload(scope.row)" 
                  style="margin-left: 10px;">下载</el-button>
                <el-button 
                  v-if="isEditMode"
                  type="text" 
                  size="small" 
                  style="color: #F56C6C; margin-left: 10px;"
                  @click="removeFile(scope.$index)">删除</el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
        <div v-else class="no-file">
          <el-empty description="暂无附件"></el-empty>
        </div>
        
        <!-- 测试结果区域 -->
        <el-divider content-position="left">
          <span class="divider-title">测试结果</span>
        </el-divider>
        
        <div class="test-result-container">
          <el-card class="result-card">
            <div class="result-summary">
              <div class="result-item">
                <span class="label">测试结果说明：</span>
                <el-input type="textarea" v-model="testResultData.description" disabled :rows="3" placeholder="暂无说明"></el-input>
              </div>
            </div>
          </el-card>
          
          <div class="report-files" v-if="testResultData.reportFiles && testResultData.reportFiles.length > 0">
            <h3 class="file-list-title">测试结果附件</h3>
            <el-table
              :data="testResultData.reportFiles"
              style="width: 100%"
              border>
              <el-table-column
                prop="name"
                label="文件名"
                min-width="300">
                <template slot-scope="scope">
                  {{ scope.row.name || scope.row.fileName || '未命名文件' }}
                </template>
              </el-table-column>
              <el-table-column
                prop="size"
                label="大小"
                width="150">
                <template slot-scope="scope">
                  {{ formatFileSize(scope.row.size) }}
                </template>
              </el-table-column>
              <el-table-column
                label="操作"
                width="150"
                align="center">
                <template slot-scope="scope">
                  <el-button 
                    type="text" 
                    size="small" 
                    @click="handlePreview(scope.row)">预览</el-button>
                  <el-button 
                    type="text" 
                    size="small" 
                    @click="handleDownload(scope.row)" 
                    style="margin-left: 10px;">下载</el-button>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </div>
        
        <!-- 发票/清单/报告/合同/其他附件信息 - 独立显示区域 -->
        <template v-if="testResultData.orderTestReportFileList && testResultData.orderTestReportFileList.length > 0">
          <el-divider content-position="left">
            <span class="divider-title">发票/清单/报告/合同/其他附件</span>
          </el-divider>
          
          <div class="test-result-container">
            <div class="report-files">
              <el-table
                :data="testResultData.orderTestReportFileList"
                style="width: 100%"
                border>
                <el-table-column
                  prop="name"
                  label="文件名"
                  min-width="300">
                </el-table-column>
                <el-table-column
                  prop="size"
                  label="大小"
                  width="150">
                  <template slot-scope="scope">
                    {{ formatFileSize(scope.row.size) }}
                  </template>
                </el-table-column>
                <el-table-column
                  label="操作"
                  width="150"
                  align="center">
                  <template slot-scope="scope">
                    <el-button 
                      type="text" 
                      size="small" 
                      @click="handlePreview(scope.row)">预览</el-button>
                    <el-button 
                      type="text" 
                      size="small" 
                      @click="handleDownload(scope.row)" 
                      style="margin-left: 10px;">下载</el-button>
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </div>
        </template>
        
        <!-- 在测试结果区域后添加争议/复测信息显示部分 -->
        <template v-if="showDisputeInfo">
          <el-divider content-position="left">
            <span class="divider-title">{{ disputeInfo.type }}信息</span>
          </el-divider>
          
          <div class="dispute-info-container">
            <el-card class="dispute-card">
              <div class="dispute-summary">
                <el-row :gutter="24">
                  <el-col :span="12">
                    <div class="dispute-item">
                      <el-form-item :label="disputeInfo.type + '说明'" label-width="120px">
                        <el-input v-model="disputeInfo.reason" type="textarea" :rows="3" disabled></el-input>
                      </el-form-item>
                    </div>
                  </el-col>
                  <el-col :span="12">
                    <div class="dispute-item">
                      <el-form-item label="提交时间" label-width="120px">
                        <el-input v-model="disputeInfo.createTime" disabled></el-input>
                      </el-form-item>
                    </div>
                  </el-col>
                </el-row>
              </div>
              
              <!-- 争议申请附件 -->
              <div v-if="disputeInfo.disputeFiles && disputeInfo.disputeFiles.length > 0" class="dispute-files">
                <h3 class="file-list-title">申请附件</h3>
                <el-table
                  :data="disputeInfo.disputeFiles"
                  style="width: 100%"
                  border>
                  <el-table-column
                    prop="name"
                    label="文件名"
                    min-width="300">
                  </el-table-column>
                  <el-table-column
                    prop="size"
                    label="大小"
                    width="150">
                    <template slot-scope="scope">
                      {{ formatFileSize(scope.row.size) }}
                    </template>
                  </el-table-column>
                  <el-table-column
                    label="操作"
                    width="150"
                    align="center">
                    <template slot-scope="scope">
                      <el-button 
                        type="text" 
                        size="small" 
                        @click="handlePreview(scope.row)">预览</el-button>
                      <el-button 
                        type="text" 
                        size="small" 
                        @click="handleDownload(scope.row)"
                        style="margin-left: 10px;">下载</el-button>
                    </template>
                  </el-table-column>
                </el-table>
              </div>

              <!-- 争议处理结果 -->
              <template v-if="disputeInfo.disputeStatus">
                <el-divider content-position="left">
                  <span class="divider-title">争议处理结果</span>
                </el-divider>
                
                <div class="dispute-result">
                  <el-row :gutter="24">
                    <el-col :span="12">
                      <div class="dispute-item">
                        <el-form-item label="处理方式" label-width="120px">
                          <el-tag :type="getDisputeStatusType(disputeInfo.disputeStatus)">
                            {{ getDisputeStatusText(disputeInfo.disputeStatus) }}
                          </el-tag>
                        </el-form-item>
                      </div>
                    </el-col>
                  </el-row>

                  <!-- 争议结果说明 -->
                  <el-row :gutter="24" v-if="disputeInfo.resultRemark">
                    <el-col :span="24">
                      <div class="dispute-item">
                        <el-form-item label="处理结果说明" label-width="120px">
                          <el-input v-model="disputeInfo.resultRemark" type="textarea" :rows="3" disabled></el-input>
                        </el-form-item>
                      </div>
                    </el-col>
                  </el-row>

                  <!-- 争议结果附件 -->
                  <div v-if="disputeInfo.resultFiles && disputeInfo.resultFiles.length > 0" class="dispute-files">
                    <h3 class="file-list-title">处理结果附件</h3>
                    <el-table
                      :data="disputeInfo.resultFiles"
                      style="width: 100%"
                      border>
                      <el-table-column
                        prop="name"
                        label="文件名"
                        min-width="300">
                      </el-table-column>
                      <el-table-column
                        prop="size"
                        label="大小"
                        width="150">
                        <template slot-scope="scope">
                          {{ formatFileSize(scope.row.size) }}
                        </template>
                      </el-table-column>
                      <el-table-column
                        label="操作"
                        width="150"
                        align="center">
                        <template slot-scope="scope">
                          <el-button 
                            type="text" 
                            size="small" 
                            @click="handlePreview(scope.row)">预览</el-button>
                          <el-button 
                            type="text" 
                            size="small" 
                            @click="handleDownload(scope.row)"
                            style="margin-left: 10px;">下载</el-button>
                        </template>
                      </el-table-column>
                    </el-table>
                  </div>
                </div>
              </template>
            </el-card>
          </div>
        </template>

        <!-- 回寄信息区域 -->
        <template v-if="showReturnInfo">
          <el-divider content-position="left">
            <span class="divider-title">{{ returnInfo.returnType === '转寄' ? '转寄信息' : '回寄信息' }}</span>
          </el-divider>
          
          <!-- 回寄信息 -->
          <template v-if="returnInfo.returnType === '回寄'">
            <el-row :gutter="24">
              <el-col :span="12">
                <el-form-item label="回寄方式">
                  <el-input v-model="returnInfo.returnMethod" disabled></el-input>
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="回寄时间">
                  <el-input v-model="returnInfo.returnTime" disabled></el-input>
                </el-form-item>
              </el-col>
            </el-row>
            
            <el-row :gutter="24">
              <el-col :span="12">
                <el-form-item label="快递方式">
                  <el-input v-model="returnInfo.expressType" disabled></el-input>
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="物流单号">
                  <el-input v-model="returnInfo.trackingNo" disabled></el-input>
                </el-form-item>
              </el-col>
            </el-row>

            <el-row :gutter="24">
              <el-col :span="12">
                <el-form-item label="回寄样品数量">
                  <el-input v-model="returnInfo.returnForwardCount" disabled></el-input>
                </el-form-item>
              </el-col>
            </el-row>

            <el-row :gutter="24">
              <el-col :span="24">
                <el-form-item label="回寄原因">
                  <el-input v-model="returnInfo.reason" type="textarea" :rows="2" disabled></el-input>
                </el-form-item>
              </el-col>
            </el-row>

            <!-- 回寄到新地址时显示回寄联系人、联系方式和地址 -->
            <template v-if="returnInfo.returnMethod === '回寄新地址'">
              <el-row :gutter="24">
                <el-col :span="12">
                  <el-form-item label="回寄联系人">
                    <el-input v-model="returnInfo.returnContact" disabled></el-input>
                  </el-form-item>
                </el-col>
                <el-col :span="12">
                  <el-form-item label="联系方式">
                    <el-input v-model="returnInfo.returnPhone" disabled></el-input>
                  </el-form-item>
                </el-col>
              </el-row>
              
              <el-row :gutter="24">
                <el-col :span="24">
                  <el-form-item label="回寄地址">
                    <el-input v-model="returnInfo.returnAddress" disabled></el-input>
                  </el-form-item>
                </el-col>
              </el-row>
            </template>
          </template>
          
          <!-- 转寄信息 -->
          <template v-else>
            <el-row :gutter="24">
              <el-col :span="24">
                <el-form-item label="转寄商家">
                  <el-input v-model="returnInfo.forwardMerchant" disabled></el-input>
                </el-form-item>
              </el-col>
            </el-row>
            
            <el-row :gutter="24">
              <el-col :span="12">
                <el-form-item label="仪器信息">
                  <el-input v-model="returnInfo.deviceName" disabled></el-input>
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="联系人">
                  <el-input v-model="returnInfo.merchantContact" disabled></el-input>
                </el-form-item>
              </el-col>
            </el-row>
            
            <el-row :gutter="24">
              <el-col :span="12">
                <el-form-item label="联系方式">
                  <el-input v-model="returnInfo.merchantPhone" disabled></el-input>
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="地址">
                  <el-input v-model="returnInfo.merchantAddress" disabled></el-input>
                </el-form-item>
              </el-col>
            </el-row>
            
            <el-row :gutter="24" v-if="!isCustomerView">
              <el-col :span="12">
                <el-form-item label="参考价格">
                  <el-input v-model="returnInfo.referencePrice" disabled>
                    <template slot="prepend">¥</template>
                  </el-input>
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="实际价格">
                  <el-input v-model="returnInfo.actualPrice" disabled>
                    <template slot="prepend">¥</template>
                  </el-input>
                </el-form-item>
              </el-col>
            </el-row>
            
            <el-row :gutter="24">
              <el-col :span="12">
                <el-form-item label="快递方式">
                  <el-input v-model="returnInfo.expressType" disabled></el-input>
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="快递单号">
                  <el-input v-model="returnInfo.trackingNo" disabled></el-input>
                </el-form-item>
              </el-col>
            </el-row>
            
            <el-row :gutter="24">
              <el-col :span="12">
                <el-form-item label="转寄时间">
                  <el-input v-model="returnInfo.returnTime" disabled></el-input>
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="转寄样品数量">
                  <el-input v-model="returnInfo.returnForwardCount" disabled></el-input>
                </el-form-item>
              </el-col>
            </el-row>
            
            <el-row :gutter="24">
              <el-col :span="24">
                <el-form-item label="转寄原因">
                  <el-input v-model="returnInfo.forwardReason" type="textarea" :rows="2" disabled></el-input>
                </el-form-item>
              </el-col>
            </el-row>
          </template>
        </template>
        
        <!-- 测试结果上传区域 -->
        <template v-if="showUploadResultArea">
          <el-divider content-position="left">
            <span class="divider-title">{{ uploadResultForm.type === 'retest' ? '复测结果上传' : '测试结果上传' }}</span>
          </el-divider>
          
          <div class="test-result-container">
            <el-form :model="uploadResultForm" ref="uploadResultForm" :rules="uploadResultRules" label-width="120px">
              <el-form-item :label="uploadResultForm.type === 'retest' ? '复测结果说明' : '测试结果说明'" prop="description">
                <el-input 
                  type="textarea" 
                  v-model="uploadResultForm.description" 
                  :rows="4"
                  :placeholder="uploadResultForm.type === 'retest' ? '请输入复测结果的详细说明' : '请输入测试结果的详细说明'">
                </el-input>
              </el-form-item>

              <el-form-item :label="uploadResultForm.type === 'retest' ? '上传复测结果' : '上传测试结果'" prop="fileList">
                <el-upload
                  class="upload-demo"
                  action="#"
                  :http-request="handleResultFileUpload"
                  :file-list="[]"
                  :on-remove="handleResultFileRemove"
                  :before-upload="beforeUploadResultFile"
                  ref="uploadResult"
                  drag
                  multiple
                  :limit="5">
                  <i class="el-icon-upload"></i>
                  <div class="el-upload__text">将文件拖到此处，或<em>点击上传</em></div>
                  <div class="el-upload__tip" slot="tip">
                    上传{{ uploadResultForm.type === 'retest' ? '复测' : '测试' }}报告、{{ uploadResultForm.type === 'retest' ? '复测' : '测试' }}图片等结果文件，支持PDF、Word、Excel、图片或压缩包(ZIP/RAR/7Z)等格式
                    <span style="color: #F56C6C; margin-left: 10px;">文件名必须包含订单号 {{ orderForm.orderNo }}</span>
                  </div>
                </el-upload>
              </el-form-item>
              
              <div class="uploaded-files" v-if="uploadResultForm.fileList.length > 0">
                <h3 class="file-list-title">已上传文件</h3>
                <el-table
                  :data="uploadResultForm.fileList"
                  style="width: 100%"
                  border>
                  <el-table-column
                    prop="name"
                    label="文件名"
                    min-width="300">
                    <template slot-scope="scope">
                      {{ scope.row.fileId || scope.row.name }}
                    </template>
                  </el-table-column>
                  <el-table-column
                    prop="size"
                    label="大小"
                    width="150">
                    <template slot-scope="scope">
                      {{ formatFileSize(scope.row.size) }}
                    </template>
                  </el-table-column>
                  <el-table-column
                    label="操作"
                    width="180"
                    align="center">
                    <template slot-scope="scope">
                      <el-button 
                        type="text" 
                        size="small" 
                        @click="handlePreview(scope.row)">预览</el-button>
                      <el-button 
                        type="text" 
                        size="small" 
                        @click="handleDownload(scope.row)"
                        style="margin-left: 10px;">下载</el-button>
                      <el-button 
                        type="text" 
                        size="small"
                        style="color: #F56C6C; margin-left: 10px;"
                        @click="handleRemoveUploadedFile(scope.$index)">删除</el-button>
                    </template>
                  </el-table-column>
                </el-table>
              </div>
              
              <el-form-item style="margin-top: 20px;">
                <el-button type="primary" @click="submitResultUploadForm">提交结果</el-button>
                <el-button @click="cancelResultUpload">取消</el-button>
              </el-form-item>
            </el-form>
          </div>
        </template>
      </el-form>
    </el-card>
  </div>
</template>

<script>
import adminApi from '@/api/adminApi'

export default {
  name: 'OrderDetail',
  data() {
    return {
      isEditMode: false,
      loading: {
        page: false,
        customers: false,
        merchants: false
      },
      originalOrderData: {
        id: '',
        customerId: '',
        merchantId: ''
      },
      orderForm: {
        projectName: '',
        customer: '',
        customerId: '',
        orderNo: '',
        contact: '',
        phone: '',
        address: '',
        sampleCount: 1,
        expectedCompletionDate: '',
        remark: '',
        merchant: '',
        merchantId: '',
        equipment: '',
        deviceId: '',
        merchantContact: '',
        merchantPhone: '',
        merchantAddress: '',
        referencePrice: '',
        actualPrice: '',
        status: '未邮寄',
        // 物流信息
        logisticsCompany: '',
        trackingNumber: '',
        shipTime: '',
        estimatedArrival: '',
        isSettled: false,
        orderFile: []
      },
      fileList: [],
      activeResultTab: 'overview',
      testResultData: {
        endTime: '',
        description: '',
        reportFiles: [],
        testReportFiles: [],
        testCheckFiles: [],
        orderTestReportFileList: []
      },
      showUploadResultArea: false,
      uploadResultForm: {
        description: '',
        fileList: [],
        type: ''
      },
      uploadResultRules: {
        description: [{ required: true, message: '请输入测试结果说明', trigger: 'blur' }]
      },
      pageTitle: '订单详情',
      showHistoryInfo: false,
      historyActivities: [],
      // 回寄信息
      showReturnInfo: false,
      returnInfo: {
        returnType: '',
        returnMethod: '',
        trackingNo: '',
        expressType: '',
        returnTime: '',
        // 转寄信息字段（当isReturnForward为0时使用）
        originalMerchant: '',
        forwardMerchant: '',
        forwardReason: '',
        // 回寄到新地址的信息（当isReturnForward为1且returnType为0时使用）
        returnContact: '',
        returnPhone: '',
        returnAddress: '',
        // 回寄原因
        reason: '',
        // 回寄/转寄样品数量
        returnForwardCount: ''
      },
      uploadHeaders: {
        Authorization: localStorage.getItem('token') || ''
      },
      uploadingFiles: 0,
      // 添加争议/复测信息数据结构
      showDisputeInfo: false,
      disputeInfo: {
        type: '', // 争议或复测
        reason: '', // 原因
        createTime: '', // 创建时间
        disputeFiles: [], // 相关附件
        disputeStatus: '', // 争议处理状态
        resultRemark: '', // 处理结果说明
        resultFiles: [] // 处理结果附件
      },
      retestResult: {
        description: '',
        reportFiles: []
      },
      // 添加合同和发票附件列表
      contractFileList: [],
      invoiceFileList: [],
      isCustomerView: false,
      isAdmin: false
    }
  },
  computed: {
    isInTransit() {
      return ['配送中', '即将到达'].includes(this.orderForm.status)
    }
  },
  created() {
    // 检查当前路径，判断是否是客户端访问
    this.isCustomerView = this.$route.path.includes('/customer/');
    // 检查当前用户角色是否为管理员
    const userRole = localStorage.getItem('userRole')
    this.isAdmin = userRole === 'admin' || userRole === 'super'
    
    // 获取订单详情
    const orderId = this.$route.params.id
    if (orderId) {
      this.fetchOrderDetail(orderId)
    }
    
    // 检查URL查询参数，处理上传测试结果或复测结果
    if (this.$route.query.action === 'upload_result') {
      this.showUploadResultArea = true
      this.uploadResultForm.type = 'test' // 默认为测试结果
    } else if (this.$route.query.action === 'upload_retest') {
      this.showUploadResultArea = true
      this.uploadResultForm.type = 'retest' // 设置为复测结果
    }
  },
  methods: {
    async fetchOrderDetail(orderId) {
      this.loading.page = true
      try {
        // 调用API获取订单详情
        const res = await adminApi.getOrderById(orderId)
        if (res) {
          console.log('获取到的订单数据:', res)
          // 添加日志，专门输出isForward和orderReturnForwardList的值
          console.log('isForward值:', res.isForward)
          console.log('orderReturnForwardList:', res.orderReturnForwardList)
          console.log('isDispute值:', res.isDispute)
          console.log('isRetest值:', res.isRetest)
          console.log('orderDisputeList:', res.orderDisputeList)
          console.log('orderContractFileList:', res.orderContractFileList)
          console.log('orderInvoiceFileList:', res.orderInvoiceFileList)
          
          const orderData = res
          
          // 保存原始ID，在编辑时使用
          this.originalOrderData = {
            id: orderId,
            customerId: orderData.customerId,
            merchantId: orderData.merchantId
          }
          
          // 映射订单数据到表单
          this.orderForm = {
            projectName: orderData.projectName || '',
            customer: orderData.customerName || '',
            customerId: orderData.customerId || '',
            orderNo: orderData.orderSn || '',
            contact: orderData.customerPerson || '',
            phone: orderData.customerPhone || '',
            address: orderData.customerAddress || '',
            sampleCount: orderData.testCount || 1,
            expectedCompletionDate: orderData.expectedEndTime || '',
            remark: orderData.remarks || '',
            merchant: orderData.merchantName || '',
            merchantId: orderData.merchantId || '',
            equipment: orderData.deviceName || '',
            deviceId: orderData.deviceId || '',
            merchantContact: orderData.merchantPerson || '',
            merchantPhone: orderData.merchantPhone || '',
            merchantAddress: orderData.merchantAddress || '',
            referencePrice: orderData.guidePrice || '',
            actualPrice: orderData.actualPrice || '',
            status: this.getStatusText(orderData.orderStatus) || '未邮寄',
            isSettled: orderData.settlementStatus === 'SETTLED',
            // 物流信息
            logisticsCompany: orderData.trackName || '',
            trackingNumber: orderData.trackSn || '',
            shipTime: orderData.trackTime || '',
            estimatedArrival: orderData.estimatedArrival || '',
            orderFile: orderData.orderFile || []
          }
          
          // 处理附件列表
          if (orderData.orderFile && Array.isArray(orderData.orderFile)) {
            console.log('原始附件数据:', orderData.orderFile)
            
            this.fileList = orderData.orderFile.map(file => {
              // 优先使用fileUrl字段，如果没有则尝试url字段
              const fileUrl = file.fileUrl || file.url || ''
              
              return {
                name: file.fileName || '未命名文件',
                size: file.fileSize || 0,
                uploadTime: file.uploadTime || new Date(),
                url: fileUrl,
                status: 'success'
              }
            })
          } else {
            this.fileList = []
          }
          
          // 处理回寄信息
          if (orderData.isBackOrder === 1 && orderData.orderReturnForwardList && orderData.orderReturnForwardList.length > 0) {
            this.showReturnInfo = true
            const returnData = orderData.orderReturnForwardList[0]
            
            // 判断是回寄还是转寄 (0是转寄，1是回寄)
            const isForward = returnData.isReturnForward === 0

            // 回寄方式：0表示新地址，1表示原路返回
            const returnType = returnData.returnType
            
            this.returnInfo = {
              returnType: isForward ? '转寄' : '回寄',
              returnMethod: returnType === 1 ? '原路返回' : '回寄新地址',
              trackingNo: returnData.trackSn || '',
              expressType: returnData.trackName || '',
              returnTime: returnData.returnForwardTime || returnData.createTime || '',
              // 转寄信息字段（当isReturnForward为0时使用）
              originalMerchant: returnData.originalMerchantName || '',
              forwardMerchant: returnData.targetMerchantName || '',
              forwardReason: returnData.reason || '',
              // 回寄到新地址的信息（当isReturnForward为1且returnType为0时使用）
              returnContact: returnData.targetMerchantPerson || '',
              returnPhone: returnData.targetMerchantPhone || '',
              returnAddress: returnData.targetMerchantAddress || '',
              // 回寄原因
              reason: returnData.reason || '',
              // 回寄/转寄样品数量
              returnForwardCount: returnData.returnForwardCount || ''
            }
            
            console.log('回寄/转寄信息:', this.returnInfo)
          }
          // 处理转寄信息
          else if ((orderData.isForward == 1 || orderData.isForward === '1') && orderData.orderReturnForwardList && orderData.orderReturnForwardList.length > 0) {
            this.showReturnInfo = true
            const returnData = orderData.orderReturnForwardList[0]
            
            this.returnInfo = {
              returnType: '转寄',
              trackingNo: returnData.trackSn || '',
              expressType: returnData.trackName || '',
              returnTime: returnData.returnForwardTime || returnData.createTime || '',
              // 转寄信息字段
              originalMerchant: returnData.originalMerchantName || '',
              forwardMerchant: returnData.targetMerchantName || '',
              forwardReason: returnData.reason || '',
              // 新增字段
              deviceName: returnData.targetDeviceName || '',
              referencePrice: returnData.targetReferencePrice || '',
              actualPrice: returnData.targetActualPrice || '', 
              merchantContact: returnData.targetMerchantPerson || '',
              merchantPhone: returnData.targetMerchantPhone || '',
              merchantAddress: returnData.targetMerchantAddress || '',
              // 回寄/转寄样品数量
              returnForwardCount: returnData.returnForwardCount || ''
            }
            
            console.log('转寄信息:', this.returnInfo)
          }
          
          // 处理测试结果数据
          if (orderData.orderResultFile && Array.isArray(orderData.orderResultFile)) {
            console.log('测试结果文件数据:', orderData.orderResultFile);
            this.testResultData = {
              description: orderData.testResultRemark || '',
              reportFiles: orderData.orderResultFile.map(file => ({
                name: file.fileName || '未命名文件',
                size: file.fileSize || 0,
                uploadTime: new Date(file.uploadTime) || new Date(),
                url: file.fileUrl || file.url || '',
                status: 'success'
              })),
              testReportFiles: [],
              testCheckFiles: [],
              orderTestReportFileList: []
            }
            
            // 处理测试报告附件
            if (orderData.orderTestReportFileList && Array.isArray(orderData.orderTestReportFileList)) {
              console.log('测试报告附件数据:', orderData.orderTestReportFileList);
              this.testResultData.testReportFiles = orderData.orderTestReportFileList.map(file => ({
                name: file.fileName || '未命名文件',
                size: file.fileSize || 0,
                uploadTime: new Date(file.uploadTime) || new Date(),
                url: file.fileUrl || file.url || '',
                status: 'success'
              }));
              
              // 将测试报告附件数据同时赋值给orderTestReportFileList
              this.testResultData.orderTestReportFileList = [...this.testResultData.testReportFiles];
            }
            
            // 处理测试清单附件
            if (orderData.orderTestCheckFileList && Array.isArray(orderData.orderTestCheckFileList)) {
              console.log('测试清单附件数据:', orderData.orderTestCheckFileList);
              this.testResultData.testCheckFiles = orderData.orderTestCheckFileList.map(file => ({
                name: file.fileName || '未命名文件',
                size: file.fileSize || 0,
                uploadTime: new Date(file.uploadTime) || new Date(),
                url: file.fileUrl || file.url || '',
                status: 'success'
              }));
              
              // 如果有测试清单数据，也添加到orderTestReportFileList中
              if (this.testResultData.testCheckFiles.length > 0) {
                this.testResultData.orderTestReportFileList = [
                  ...this.testResultData.orderTestReportFileList,
                  ...this.testResultData.testCheckFiles
                ];
              }
            }
            
            // 处理合同和发票附件
            if (orderData.orderContractFileList && Array.isArray(orderData.orderContractFileList)) {
              const contractFiles = orderData.orderContractFileList.map(file => ({
                name: file.fileName || '未命名文件(合同)',
                size: file.fileSize || 0,
                uploadTime: file.uploadTime || new Date(),
                url: file.fileUrl || file.url || '',
                status: 'success'
              }));
              
              // 将合同附件添加到orderTestReportFileList
              this.testResultData.orderTestReportFileList = [
                ...this.testResultData.orderTestReportFileList,
                ...contractFiles
              ];
            }
            
            if (orderData.orderInvoiceFileList && Array.isArray(orderData.orderInvoiceFileList)) {
              const invoiceFiles = orderData.orderInvoiceFileList.map(file => ({
                name: file.fileName || '未命名文件(发票)',
                size: file.fileSize || 0,
                uploadTime: file.uploadTime || new Date(),
                url: file.fileUrl || file.url || '',
                status: 'success'
              }));
              
              // 将发票附件添加到orderTestReportFileList
              this.testResultData.orderTestReportFileList = [
                ...this.testResultData.orderTestReportFileList,
                ...invoiceFiles
              ];
            }
            
            console.log('处理后的测试结果数据:', this.testResultData);
          } else {
            // 如果没有测试结果文件，初始化测试结果数据基本结构但不清空orderTestReportFileList
            this.testResultData = {
              description: '',
              reportFiles: [],
              testReportFiles: [],
              testCheckFiles: [],
              orderTestReportFileList: []
            }
            
            // 即使没有测试结果文件，也单独处理其他附件
            // 处理测试报告附件
            if (orderData.orderTestReportFileList && Array.isArray(orderData.orderTestReportFileList)) {
              console.log('没有测试结果但有测试报告附件数据:', orderData.orderTestReportFileList);
              this.testResultData.testReportFiles = orderData.orderTestReportFileList.map(file => ({
                name: file.fileName || '未命名文件',
                size: file.fileSize || 0,
                uploadTime: new Date(file.uploadTime) || new Date(),
                url: file.fileUrl || file.url || '',
                status: 'success'
              }));
              
              // 将测试报告附件数据同时赋值给orderTestReportFileList
              this.testResultData.orderTestReportFileList = [...this.testResultData.testReportFiles];
            }
            
            // 处理测试清单附件
            if (orderData.orderTestCheckFileList && Array.isArray(orderData.orderTestCheckFileList)) {
              console.log('没有测试结果但有测试清单附件数据:', orderData.orderTestCheckFileList);
              this.testResultData.testCheckFiles = orderData.orderTestCheckFileList.map(file => ({
                name: file.fileName || '未命名文件',
                size: file.fileSize || 0,
                uploadTime: new Date(file.uploadTime) || new Date(),
                url: file.fileUrl || file.url || '',
                status: 'success'
              }));
              
              // 如果有测试清单数据，也添加到orderTestReportFileList中
              if (this.testResultData.testCheckFiles.length > 0) {
                this.testResultData.orderTestReportFileList = [
                  ...this.testResultData.orderTestReportFileList,
                  ...this.testResultData.testCheckFiles
                ];
              }
            }
            
            // 处理合同和发票附件
            if (orderData.orderContractFileList && Array.isArray(orderData.orderContractFileList)) {
              console.log('没有测试结果但有合同附件数据:', orderData.orderContractFileList);
              const contractFiles = orderData.orderContractFileList.map(file => ({
                name: file.fileName || '未命名文件(合同)',
                size: file.fileSize || 0,
                uploadTime: file.uploadTime || new Date(),
                url: file.fileUrl || file.url || '',
                status: 'success'
              }));
              
              // 将合同附件添加到orderTestReportFileList
              this.testResultData.orderTestReportFileList = [
                ...this.testResultData.orderTestReportFileList,
                ...contractFiles
              ];
            }
            
            if (orderData.orderInvoiceFileList && Array.isArray(orderData.orderInvoiceFileList)) {
              console.log('没有测试结果但有发票附件数据:', orderData.orderInvoiceFileList);
              const invoiceFiles = orderData.orderInvoiceFileList.map(file => ({
                name: file.fileName || '未命名文件(发票)',
                size: file.fileSize || 0,
                uploadTime: file.uploadTime || new Date(),
                url: file.fileUrl || file.url || '',
                status: 'success'
              }));
              
              // 将发票附件添加到orderTestReportFileList
              this.testResultData.orderTestReportFileList = [
                ...this.testResultData.orderTestReportFileList,
                ...invoiceFiles
              ];
            }
            
            console.log('没有测试结果文件但处理了其他附件:', this.testResultData.orderTestReportFileList);
          }
          
          // 处理复测结果数据
          if (orderData.isRetest === 1) {
            console.log('开始处理复测结果数据');
            console.log('orderRetestResult:', orderData.orderRetestResult);
            console.log('orderRetestResultFileList:', orderData.orderRetestResultFileList);
            
            // 尝试从多个可能的数据源获取复测结果
            const retestResultData = orderData.orderRetestResult || 
                                    (orderData.orderRetestList && orderData.orderRetestList.length > 0 && orderData.orderRetestList[0]) || 
                                    {};
            
            // 尝试从多个可能的数据源获取复测结果文件列表
            const retestResultFiles = orderData.orderRetestResultFileList || 
                                     (orderData.orderRetestList && orderData.orderRetestList.length > 0 && 
                                      orderData.orderRetestList[0].resultFiles) || 
                                     [];
            
            console.log('处理后的retestResultData:', retestResultData);
            console.log('处理后的retestResultFiles:', retestResultFiles);
            
            this.retestResult = {
              description: retestResultData.retestRemark || retestResultData.remark || '',
              reportFiles: (Array.isArray(retestResultFiles) ? retestResultFiles : []).map(file => ({
                name: file.fileName || '未命名文件',
                size: file.fileSize || 0,
                uploadTime: file.uploadTime || new Date(),
                url: file.fileUrl || file.url || '',
                status: 'success'
              }))
            }
            console.log('最终的复测结果数据:', this.retestResult);
          } else {
            this.retestResult = null;
          }
          
          // 添加更详细的日志，显示复测相关字段
          console.log('复测相关字段检查:');
          console.log('isRetest:', orderData.isRetest);
          console.log('orderRetestList:', orderData.orderRetestList);
          console.log('orderRetestFileList:', orderData.orderRetestFileList);
            
          // 处理争议/复测信息
          if (((orderData.isRetest === 1 && orderData.orderRetestList && orderData.orderRetestList.length > 0) || 
               (orderData.isDispute === 1 && orderData.orderDisputeList && orderData.orderDisputeList.length > 0))) {
            
            this.showDisputeInfo = true;
            
            // 优先显示复测信息，其次显示争议信息
            if (orderData.isRetest === 1 && orderData.orderRetestList && orderData.orderRetestList.length > 0) {
              console.log('满足复测信息显示条件，准备显示复测信息');
              const disputeData = orderData.orderRetestList[0]; // 获取最新的一条记录
              console.log('复测记录数据:', disputeData);
              
              // 复测文件列表
              const disputeFiles = orderData.orderRetestFileList || [];
              
              this.disputeInfo = {
                type: '复测',
                reason: disputeData.retestRemark || disputeData.disputeRemark || disputeData.remark || '',
                createTime: disputeData.createTime || '',
                disputeFiles: disputeFiles.map(file => ({
                  name: file.fileName || '未命名文件',
                  size: file.fileSize || 0,
                  uploadTime: file.uploadTime || new Date(),
                  url: file.fileUrl || file.url || '',
                  status: 'success'
                })),
                disputeStatus: disputeData.retestStatus || '',
                resultRemark: disputeData.retestRemark || '',
                resultFiles: disputeData.orderRetestResultFileList || []
              };
              
              console.log('处理后的复测信息:', this.disputeInfo);
            } else if (orderData.isDispute === 1 && orderData.orderDisputeList && orderData.orderDisputeList.length > 0) {
              const disputeData = orderData.orderDisputeList[0]; // 获取最新的一条记录
              
              // 争议文件列表
              const disputeFiles = disputeData.orderDisputeFile || [];
              
              // 处理争议结果附件数据，确保是数组格式并进行正确的数据映射
              const resultFiles = (Array.isArray(disputeData.orderDisputeResultFile) ? 
                disputeData.orderDisputeResultFile : 
                (disputeData.orderDisputeResultFile ? [disputeData.orderDisputeResultFile] : [])
              ).map(file => ({
                name: file.fileName || '未命名文件',
                size: file.fileSize || 0,
                uploadTime: file.uploadTime || new Date(),
                url: file.fileUrl || file.url || '',
                status: 'success'
              }));
              
              this.disputeInfo = {
                type: '争议',
                reason: disputeData.disputeRemark || '',
                createTime: disputeData.createTime || '',
                disputeFiles: disputeFiles.map(file => ({
                  name: file.fileName || '未命名文件',
                  size: file.fileSize || 0,
                  uploadTime: file.uploadTime || new Date(),
                  url: file.fileUrl || file.url || '',
                  status: 'success'
                })),
                disputeStatus: disputeData.disputeStatus || '',
                resultRemark: disputeData.resultRemark || '',
                resultFiles: resultFiles
              };
              
              console.log('争议信息:', this.disputeInfo);
              console.log('争议结果附件:', resultFiles);
            }
          }
        } else {
          this.$message.error('获取订单详情失败')
        }
      } catch (error) {
        console.error('获取订单详情失败:', error)
        this.$message.error('获取订单详情失败: ' + (error.message || '未知错误'))
      } finally {
        this.loading.page = false
      }
    },
    getStatusType(status) {
      const statusMap = {
        '未邮寄': 'info',
        '配送中': 'primary',
        '即将到达': 'success',
        '已完成': 'success',
        '已取消': 'danger'
      }
      return statusMap[status] || 'info'
    },
    // 添加getStatusText方法，用于转换订单状态码为中文状态文本
    getStatusText(status) {
      const statusTextMap = {
        'NOT_SHIPPED': '未邮寄',
        'IN_TRANSIT': '在途',
        'TESTING': '测试中',
        'TESTED': '已测试',
        'DISPUTE': '复测/争议',
        'RETURN': '回寄',
        'FORWARD': '转寄',
        'UNSETTLED': '未结算',
        'COMPLETED': '已完成',
        'CANCEL': '已取消'
      }
      return statusTextMap[status] || status
    },
    handlePreview(file) {
      // 调用统一的预览方法
      adminApi.previewFile(file, this.$message)
        .catch(error => {
          console.error('文件预览失败:', error);
        });
    },
    handleDownload(file) {
      // 调用统一的下载方法
      adminApi.downloadFileUnified(file, this.$message)
        .catch(error => {
          console.error('文件下载失败:', error);
        });
    },
    formatFileSize(size) {
      if (!size) return '0 B'
      const units = ['B', 'KB', 'MB', 'GB']
      let index = 0
      while (size >= 1024 && index < units.length - 1) {
        size /= 1024
        index++
      }
      return size.toFixed(2) + ' ' + units[index]
    },
    // 编辑订单按钮处理方法
    handleEdit() {
      // 跳转到一个新的路由，专门用于编辑商家信息和物流信息
      this.$router.push(`/admin/orders/edit-merchant/${this.originalOrderData.id}`)
    },
    
    // 测试结果文件上传处理
    handleResultFileUpload(options) {
      const { file } = options;
      
      // 再次检查文件名是否包含订单号
      if (this.orderForm.orderNo && !file.name.includes(this.orderForm.orderNo)) {
        console.log('文件名不包含订单号，已在beforeUpload中拦截，不执行上传');
        return;
      }
      
      this.uploadingFiles++;
      
      // 调用上传接口 - 直接传递file对象而不是formData
      adminApi.uploadFile(file, this.originalOrderData.customerId, (percent) => {
        console.log(`上传进度: ${parseInt(percent)}%`);
      }).then(res => {
        this.uploadingFiles--;
        if (res) {
          const fileData = res;
          
          // 确保URL是完整的URL
          let fileUrl = fileData.url || fileData.fileUrl;
          if (fileUrl && !fileUrl.startsWith('http') && !fileUrl.startsWith('blob:')) {
            fileUrl = adminApi.ApiUrl + (fileUrl.startsWith('/') ? '' : '/') + fileUrl;
          }
          
          // 创建文件对象
          const newFile = {
            name: file.name,
            size: file.size,
            uploadTime: new Date(),
            url: fileUrl,
            fileId: fileData.fileId || fileData.id,
            path: fileData.path || '',
            status: 'success'
          };
          
          // 将文件添加到上传列表
          this.uploadResultForm.fileList.push(newFile);
          this.$message.success(`文件 ${file.name} 上传成功`);
        } else {
          this.$message.error(`文件 ${file.name} 上传失败，请重试`);
        }
      }).catch(error => {
        this.uploadingFiles--;
        console.error('文件上传失败:', error);
        this.$message.error(`文件 ${file.name} 上传失败: ${error.message || '未知错误'}`);
      });
    },
    
    // 测试结果文件移除处理
    handleResultFileRemove(file) {
      // 从文件列表中移除文件
      const index = this.uploadResultForm.fileList.findIndex(item => 
        (item.uid === file.uid) || (item.name === file.name && item.size === file.size)
      );
      
      if (index !== -1) {
        this.uploadResultForm.fileList.splice(index, 1);
      }
    },
    
    // 测试结果文件上传前校验
    beforeUploadResultFile(file) {
      // 检查文件大小
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        this.$message.error('文件大小不能超过10MB!');
        return false;
      }
      
      // 可以在这里添加文件类型检查
      const allowedTypes = [
        'application/pdf', 
        'application/msword', 
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'image/jpeg',
        'image/png',
        'image/gif',
        'application/zip',
        'application/x-zip-compressed',
        'application/x-rar-compressed',
        'application/octet-stream',
        'application/x-7z-compressed'
      ];
      
      // 获取文件扩展名
      const fileName = file.name;
      const fileExtension = fileName.substring(fileName.lastIndexOf('.') + 1).toLowerCase();
      const allowedExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'jpg', 'jpeg', 'png', 'gif', 'zip', 'rar', '7z'];
      
      // 同时检查MIME类型和文件扩展名
      const isAllowedType = allowedTypes.includes(file.type) || allowedExtensions.includes(fileExtension);
      
      if (!isAllowedType) {
        this.$message.warning('请上传PDF、Word、Excel、图片或压缩包(ZIP/RAR/7Z)格式的文件');
        console.log('文件类型不允许:', file.type, fileExtension);
        // 返回false明确阻止上传
        return false;
      }
      
      // 检查文件是否包含订单号，如果不包含则阻止上传
      if (this.orderForm.orderNo && !file.name.includes(this.orderForm.orderNo)) {
        this.$message.error(`文件名 ${file.name} 不包含订单号 ${this.orderForm.orderNo}，请重命名后上传`);
        return false;
      }
      
      return true;
    },
    
    // 删除已上传文件
    handleRemoveUploadedFile(index) {
      this.uploadResultForm.fileList.splice(index, 1);
    },
    
    // 提交测试结果
    submitResultUploadForm() {
      this.$refs.uploadResultForm.validate(valid => {
        if (valid) {
          if (this.uploadResultForm.fileList.length === 0) {
            this.$message.warning('请至少上传一个结果文件');
            return;
          }
          
          // 如果还有文件正在上传，提示等待
          if (this.uploadingFiles > 0) {
            this.$message.warning(`还有 ${this.uploadingFiles} 个文件正在上传，请等待上传完成后再提交`);
            return;
          }
          
          const params = {
            id: this.originalOrderData.id,
            orderStatus: this.uploadResultForm.type === 'retest' ? 'RETESTING' : 'TESTING',
            // 根据类型设置不同的备注字段
            ...(this.uploadResultForm.type === 'retest' 
              ? { retestRemark: this.uploadResultForm.description }
              : { testResultRemark: this.uploadResultForm.description }
            ),
            // 根据类型设置不同的文件列表字段
            ...(this.uploadResultForm.type === 'retest'
              ? {
                  orderRetestResultFileList: this.uploadResultForm.fileList.map(file => ({
                    fileId: file.fileId,
                    filePath: file.path,
                    fileName: file.name,
                    fileUrl: file.url,
                    fileSize: file.size
                  }))
                }
              : {
                  orderResultFile: this.uploadResultForm.fileList.map(file => ({
                    fileId: file.fileId,
                    filePath: file.path,
                    fileName: file.name,
                    fileUrl: file.url,
                    fileSize: file.size
                  }))
                }
            )
          };
          
          // 调用接口提交结果
          adminApi.editOrder(params)
            .then(res => {
              if (res) {
                this.$message.success(this.uploadResultForm.type === 'retest' ? '复测结果提交成功' : '测试结果提交成功');
                // 根据类型跳转到不同的页面
                this.$router.push(this.uploadResultForm.type === 'retest' ? '/admin/orders/retesting' : '/admin/orders/tested');
              } else {
                this.$message.error(this.uploadResultForm.type === 'retest' ? '复测结果提交失败' : '测试结果提交失败');
              }
            })
            .catch(error => {
              console.error('提交结果失败:', error);
              this.$message.error('提交结果失败: ' + (error.message || '未知错误'));
            });
        } else {
          this.$message.warning('请完善结果信息');
          return false;
        }
      });
    },
    
    // 取消上传测试结果
    cancelResultUpload() {
      this.uploadResultForm = {
        description: '',
        fileList: [],
        type: ''
      };
      this.showUploadResultArea = false;
    },
    
    // 添加跳转到上传测试报告和清单页面的方法
    goToUploadReports() {
      this.$router.push(`/admin/orders/reports/upload/${this.originalOrderData.id}`)
    },
    getDisputeStatusType(status) {
      const statusMap = {
        'PASS': 'success',
        'REJECT': 'danger',
        'RETESTING': 'warning'
      }
      return statusMap[status] || 'info'
    },
    getDisputeStatusText(status) {
      const statusTextMap = {
        'PASS': '解决争议',
        'REJECT': '驳回争议',
        'RETESTING': '复测'
      }
      return statusTextMap[status] || status
    }
  }
}
</script>

<style scoped>
.order-detail-container {
  padding: 30px;
  background-color: #f5f7fa;
  min-height: 100vh;
}

.order-card {
  width: 100%;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

/* 添加全局样式，统一文本域中的字体样式 */
.order-detail-container .el-textarea__inner {
  font-family: inherit;
  font-size: 14px;
  line-height: 1.5;
  color: #606266;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
}

.page-header h2 {
  margin: 0;
  color: #303133;
  font-size: 24px;
}

.divider-title {
  font-size: 18px;
  font-weight: bold;
  color: #409EFF;
  position: relative;
  padding-left: 12px;
}

.divider-title::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  height: 18px;
  width: 4px;
  background-color: #409EFF;
  border-radius: 2px;
}

.el-form-item {
  margin-bottom: 24px;
}

.el-form-item__label {
  font-weight: bold;
  color: #606266;
  white-space: nowrap;
  padding-right: 10px;
}

.file-list-container {
  margin-top: 20px;
  margin-bottom: 20px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background-color: #fff;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
  padding: 20px;
}

.file-list-title {
  margin: 0 0 20px 0;
  font-size: 16px;
  color: #303133;
  position: relative;
  padding-left: 12px;
  border-left: 3px solid #409EFF;
}

.no-file {
  padding: 40px 0;
}

.el-timeline {
  padding: 0;
  margin: 0;
}

.test-result-container {
  margin-top: 20px;
  margin-bottom: 20px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background-color: #fff;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
  padding: 20px;
}

.result-card {
  margin-bottom: 20px;
}

.result-header {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 15px;
}

.result-summary {
  margin-bottom: 20px;
}

.result-item {
  margin-bottom: 10px;
}

.label {
  font-weight: bold;
  color: #606266;
  white-space: nowrap;
  padding-right: 10px;
}

.test-stats {
  margin-bottom: 20px;
}

.stat-card {
  margin-bottom: 10px;
}

.stat-title {
  font-weight: bold;
  color: #606266;
  white-space: nowrap;
  padding-right: 10px;
}

.stat-value {
  font-weight: normal;
  color: #606266;
  white-space: nowrap;
}

.success {
  color: #67C23A;
}

.report-files {
  margin-top: 20px;
}

.el-upload__text {
  color: #606266;
  font-size: 14px;
  text-align: center;
}

.el-upload__text em {
  color: #409EFF;
  font-style: normal;
}

.test-description {
  color: #606266;
  line-height: 1.5;
  margin-top: 5px;
  white-space: pre-wrap;
  word-break: break-word;
}

.status-tag {
  margin-left: 0;
  display: block;
  text-align: left;
  width: fit-content;
}

.history-info-container {
  margin-top: 20px;
  margin-bottom: 20px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background-color: #fff;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
  padding: 20px;
}

.history-title {
  margin: 0 0 20px 0;
  font-size: 16px;
  color: #303133;
  position: relative;
  padding-left: 12px;
  border-left: 3px solid #409EFF;
}

.history-item {
  margin-bottom: 10px;
}

.file-label {
  font-weight: bold;
  color: #606266;
  white-space: nowrap;
  padding-right: 10px;
}

.file-link {
  margin-left: 10px;
}

.form-actions {
  margin-top: 20px;
  text-align: center;
}

.form-actions .el-button {
  padding: 12px 30px;
  font-size: 16px;
}

/* 确保所有textarea统一字体样式 */
::v-deep .el-textarea__inner {
  font-family: inherit !important;
  font-size: 14px !important;
  line-height: 1.5 !important;
  color: #606266 !important;
}

.upload-area {
  margin-bottom: 20px;
}

.el-upload__tip {
  color: #909399;
  font-size: 12px;
  margin-top: 5px;
}

.dispute-info-container {
  margin-top: 20px;
  margin-bottom: 20px;
}

.dispute-card {
  margin-bottom: 20px;
}

.dispute-summary {
  margin-bottom: 20px;
}

.dispute-item {
  margin-bottom: 10px;
}

.dispute-files {
  margin-top: 20px;
}

.retest-result-container {
  margin-top: 20px;
  margin-bottom: 20px;
}

.retest-card {
  margin-bottom: 20px;
}

.retest-summary {
  margin-bottom: 20px;
}

.retest-item {
  margin-bottom: 10px;
}

.contract-files {
  margin-top: 20px;
  margin-bottom: 20px;
}

.invoice-files {
  margin-top: 20px;
  margin-bottom: 20px;
}
</style>